import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Play, BookOpen, Brain, Clock, Target, CheckCircle, X, RotateCcw } from 'lucide-react';
import { mathQuizData, getQuizByTopicId, getQuizDifficulty } from '../data/mathQuiz';
import MathExpression from './MathExpression';

const QuizletIntegration = ({ selectedTopic, onBack }) => {
  const [currentView, setCurrentView] = useState('overview');
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [userAnswers, setUserAnswers] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState({ correct: 0, wrong: 0 });
  const [timeLeft, setTimeLeft] = useState(0);
  const [timerActive, setTimerActive] = useState(false);

  // No MathJax needed - using HTML renderer to eliminate flickering

  // Get quiz for the selected topic
  const topicQuiz = useMemo(() => {
    if (selectedTopic) {
      return getQuizByTopicId(selectedTopic.id);
    }
    return null;
  }, [selectedTopic]);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0 && timerActive) {
      handleQuizComplete();
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);

  // Initialize quiz
  const startQuiz = (quiz) => {
    setSelectedQuiz(quiz);
    setCurrentQuestion(0);
    setSelectedAnswer('');
    setUserAnswers([]);
    setShowResult(false);
    setScore({ correct: 0, wrong: 0 });
    setTimeLeft(quiz.timeLimit);
    setTimerActive(true);
    setCurrentView('quiz');
  };

  // Handle answer selection
  const handleAnswerSelect = (answer) => {
    if (showResult) return;
    
    setSelectedAnswer(answer);
    const currentQ = selectedQuiz.questions[currentQuestion];
    const isCorrect = answer === (currentQ.correct || currentQ.correctAnswer);
    
    const answerData = {
      questionId: currentQ.id,
      question: currentQ.question,
      selectedAnswer: answer,
              correctAnswer: currentQ.correct || currentQ.correctAnswer,
      isCorrect: isCorrect,
      explanation: currentQ.explanation
    };
    
    setUserAnswers(prev => [...prev, answerData]);
    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      wrong: prev.wrong + (isCorrect ? 0 : 1)
    }));
    
    setShowResult(true);
    
    // Auto-advance after 2 seconds
    setTimeout(() => {
      if (currentQuestion < selectedQuiz.questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedAnswer('');
        setShowResult(false);
      } else {
        handleQuizComplete();
      }
    }, 2000);
  };

  // Handle quiz completion
  const handleQuizComplete = () => {
    setTimerActive(false);
    setCurrentView('results');
  };

  // Reset quiz
  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer('');
    setUserAnswers([]);
    setShowResult(false);
    setScore({ correct: 0, wrong: 0 });
    setTimeLeft(selectedQuiz.timeLimit);
    setTimerActive(true);
    setCurrentView('quiz');
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Overview View
  const OverviewView = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Essays
          </button>
          <div className="flex items-center gap-2 text-blue-600">
            <Brain className="w-5 h-5" />
            <span className="font-semibold">Essay Quiz</span>
          </div>
        </div>

        {selectedTopic && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              測驗課題：{selectedTopic.title}
            </h2>
            <p className="text-gray-600 mb-4">{selectedTopic.description}</p>
            
            {topicQuiz ? (
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-200">
                <div className="flex items-start gap-4">
                  <div className="text-4xl">📊</div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {topicQuiz.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{topicQuiz.description}</p>
                    
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">{topicQuiz.totalQuestions}</div>
                        <div className="text-sm text-gray-600">題目</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{Math.round(topicQuiz.timeLimit / 60)}</div>
                        <div className="text-sm text-gray-600">分鐘</div>
                      </div>
                      <div className="text-center">
                        <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          {topicQuiz.difficulty === 'beginner' ? '初級' : topicQuiz.difficulty === 'intermediate' ? '中級' : '高級'}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {topicQuiz.category}
                        </div>
                        <div className="text-sm text-gray-600">分類</div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => startQuiz(topicQuiz)}
                      className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-semibold"
                    >
                      <Play className="w-5 h-5" />
                      Start Quiz
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-4">
                  <BookOpen className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Quiz Available</h3>
                <p className="text-gray-500">
                  A quiz for this essay template is not yet available. Check back later!
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* All Available Quizzes */}
      <div className="bg-white p-6 rounded-xl shadow-sm border">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">All Available Quizzes</h3>
        <div className="grid gap-4 lg:grid-cols-2">
          {mathQuizData.map((quiz) => (
            <div key={quiz.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="text-3xl">{quiz.image}</div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-1">{quiz.title}</h4>
                  <p className="text-sm text-gray-600 mb-2">{quiz.description}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Target className="w-3 h-3" />
                      {quiz.totalQuestions} questions
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {Math.round(quiz.timeLimit / 60)} min
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs ${getQuizDifficulty(quiz.difficulty).color}`}>
                      {quiz.difficulty}
                    </span>
                  </div>
                  <button
                    onClick={() => startQuiz(quiz)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Start Quiz
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Quiz View
  const QuizView = () => {
    if (!selectedQuiz) return null;
    
    const currentQ = selectedQuiz.questions[currentQuestion];
    const progress = ((currentQuestion + 1) / selectedQuiz.questions.length) * 100;
    
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {/* Quiz Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setCurrentView('overview')}
                className="flex items-center gap-2 text-blue-100 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
                Exit Quiz
              </button>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span className="font-mono text-lg">{formatTime(timeLeft)}</span>
                </div>
                <div className="text-sm">
                  {currentQuestion + 1} / {selectedQuiz.questions.length}
                </div>
              </div>
            </div>
            
            <div className="w-full bg-blue-500 rounded-full h-2">
              <div 
                className="bg-white h-2 rounded-full transition-all duration-300"
                style={{width: `${progress}%`}}
              ></div>
            </div>
          </div>

          {/* Question Content */}
          <div className="p-8">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">{selectedQuiz.image}</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2 quiz-question">
                <MathExpression>{currentQ.question}</MathExpression>
              </h2>
              {currentQ.question_text && (
                <p className="text-gray-600">{currentQ.question_text}</p>
              )}
            </div>

            {/* Answer Options */}
            <div className="space-y-3 max-w-2xl mx-auto">
              {currentQ.type === 'multiple-choice' && currentQ.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(typeof option === 'object' ? option.id : option)}
                  disabled={showResult}
                  className={`w-full p-4 rounded-xl text-left border transition-all ${
                                          showResult
                      ? (typeof option === 'object' ? option.id : option) === (currentQ.correct || currentQ.correctAnswer)
                        ? 'bg-green-100 border-green-500 text-green-800'
                        : selectedAnswer === (typeof option === 'object' ? option.id : option)
                        ? 'bg-red-100 border-red-500 text-red-800'
                        : 'bg-gray-50 border-gray-200 text-gray-600'
                      : selectedAnswer === (typeof option === 'object' ? option.id : option)
                      ? 'bg-blue-100 border-blue-500 text-blue-800'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      showResult && (typeof option === 'object' ? option.id : option) === (currentQ.correct || currentQ.correctAnswer)
                        ? 'border-green-500 bg-green-500'
                        : showResult && selectedAnswer === (typeof option === 'object' ? option.id : option) && (typeof option === 'object' ? option.id : option) !== (currentQ.correct || currentQ.correctAnswer)
                        ? 'border-red-500 bg-red-500'
                        : selectedAnswer === (typeof option === 'object' ? option.id : option)
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {showResult && (typeof option === 'object' ? option.id : option) === (currentQ.correct || currentQ.correctAnswer) && (
                        <CheckCircle className="w-4 h-4 text-white" />
                      )}
                      {showResult && selectedAnswer === (typeof option === 'object' ? option.id : option) && (typeof option === 'object' ? option.id : option) !== (currentQ.correct || currentQ.correctAnswer) && (
                        <X className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <span className="font-medium quiz-option">
                      <MathExpression>{typeof option === 'object' ? option.text : option}</MathExpression>
                    </span>
                  </div>
                </button>
              ))}

              {currentQ.type === 'true-false' && (
                <div className="grid grid-cols-2 gap-4">
                  {['True', 'False'].map((option) => (
                    <button
                      key={option}
                      onClick={() => handleAnswerSelect(option.toLowerCase() === 'true')}
                      disabled={showResult}
                      className={`p-4 rounded-xl border transition-all ${
                        showResult
                          ? (option.toLowerCase() === 'true') === currentQ.correct
                            ? 'bg-green-100 border-green-500 text-green-800'
                            : selectedAnswer === (option.toLowerCase() === 'true')
                            ? 'bg-red-100 border-red-500 text-red-800'
                            : 'bg-gray-50 border-gray-200 text-gray-600'
                          : selectedAnswer === (option.toLowerCase() === 'true')
                          ? 'bg-blue-100 border-blue-500 text-blue-800'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <div className="text-center font-medium">{option}</div>
                    </button>
                  ))}
                </div>
              )}

              {currentQ.type === 'fill-in-blank' && (
                <div className="text-center">
                  <input
                    type="text"
                    value={selectedAnswer}
                    onChange={(e) => setSelectedAnswer(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAnswerSelect(selectedAnswer)}
                    placeholder="Type your answer here..."
                    className="w-full max-w-md p-4 border border-gray-300 rounded-xl text-center text-lg font-medium focus:border-blue-500 focus:ring-blue-500"
                    disabled={showResult}
                  />
                  {!showResult && (
                    <button
                      onClick={() => handleAnswerSelect(selectedAnswer)}
                      disabled={!selectedAnswer.trim()}
                      className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                      Submit Answer
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Explanation */}
            {showResult && (
              <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200 max-w-2xl mx-auto">
                <h4 className="font-semibold text-blue-900 mb-2">Explanation:</h4>
                <p className="text-blue-800">
                  <MathExpression>{currentQ.explanation}</MathExpression>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Results View
  const ResultsView = () => {
    const percentage = Math.round((score.correct / selectedQuiz.questions.length) * 100);
    const passed = percentage >= 70;
    
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {/* Results Header */}
          <div className={`${passed ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-orange-500 to-red-600'} text-white p-8 text-center`}>
            <div className="text-6xl mb-4">{passed ? '🎉' : '📚'}</div>
            <h2 className="text-3xl font-bold mb-2">
              {passed ? 'Congratulations!' : 'Good Effort!'}
            </h2>
            <p className="text-lg opacity-90">
              {passed ? 'You passed the quiz!' : 'Keep practicing to improve!'}
            </p>
          </div>

          {/* Score Summary */}
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <div className="text-3xl font-bold text-blue-600 mb-2">{percentage}%</div>
                <div className="text-gray-600">Overall Score</div>
              </div>
              <div className="text-center p-6 bg-green-50 rounded-xl">
                <div className="text-3xl font-bold text-green-600 mb-2">{score.correct}</div>
                <div className="text-gray-600">Correct Answers</div>
              </div>
              <div className="text-center p-6 bg-red-50 rounded-xl">
                <div className="text-3xl font-bold text-red-600 mb-2">{score.wrong}</div>
                <div className="text-gray-600">Incorrect Answers</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center mb-8">
              <button
                onClick={resetQuiz}
                className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                <RotateCcw className="w-5 h-5" />
                Retake Quiz
              </button>
              <button
                onClick={() => setCurrentView('overview')}
                className="flex items-center gap-2 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors font-semibold"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Overview
              </button>
            </div>

            {/* Detailed Results */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Question Review</h3>
              {userAnswers.map((answer, index) => (
                <div key={index} className={`p-4 rounded-lg border ${answer.isCorrect ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${answer.isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>
                      {answer.isCorrect ? (
                        <CheckCircle className="w-5 h-5 text-white" />
                      ) : (
                        <X className="w-5 h-5 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        Question {index + 1}: <MathExpression>{answer.question}</MathExpression>
                      </h4>
                      <div className="text-sm space-y-1">
                        <div>
                          <span className="font-medium">Your answer:</span> 
                          <span className={answer.isCorrect ? 'text-green-700' : 'text-red-700'}>
                            {typeof answer.selectedAnswer === 'boolean' ? (answer.selectedAnswer ? 'True' : 'False') : answer.selectedAnswer}
                          </span>
                        </div>
                        {!answer.isCorrect && (
                          <div>
                            <span className="font-medium">Correct answer:</span> 
                            <span className="text-green-700">
                              {typeof answer.correctAnswer === 'boolean' ? (answer.correctAnswer ? 'True' : 'False') : answer.correctAnswer}
                            </span>
                          </div>
                        )}
                        <div className="text-gray-600 mt-2">
                          <span className="font-medium">Explanation:</span> <MathExpression>{answer.explanation}</MathExpression>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div 
        className="relative bg-blue-600 text-white p-8"
        style={{
          backgroundImage: 'linear-gradient(rgba(37, 99, 235, 0.8), rgba(37, 99, 235, 0.8)), url(https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <Brain className="w-8 h-8" />
            <h1 className="text-4xl font-bold">EssayRight Quiz</h1>
          </div>
          <p className="text-blue-100 text-lg">Test your writing skills with interactive quizzes</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {currentView === 'overview' && <OverviewView />}
        {currentView === 'quiz' && <QuizView />}
        {currentView === 'results' && <ResultsView />}
      </div>
    </div>
  );
};

export default QuizletIntegration; 